<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.countries.form.country_code')"
            :is-required="true"
            :hint="__('settings.countries.form.country_code_hint')"
        >
            <DInput
                id="country_code"
                v-model="form.country_code"
                :placeholder="__('settings.countries.form.country_code')"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('country_code')"
                :invalid-feedback="errors.country_code"
                maxlength="2"
                :disabled="isCountryCodeDisabled() || onlyShowForm"
            />
        </DFormRow>
        <DFormRow
            v-for="language in languages"
            :key="language"
            :label="__(`settings.countries.form.name_${language}`)"
            :is-required="true"
            :hint="__('settings.countries.form.name_hint')"
        >
            <DInput
                :id="`name_${language}`"
                v-model="form[`name_${language}`]"
                :placeholder="__(`settings.countries.form.name_${language}`)"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid(`name_${language}`)"
                :invalid-feedback="errors[`name_${language}`]"
                maxlength="128"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.countries.form.is_eu')"
            :error="errors.is_eu"
            :hint="__('settings.countries.form.is_eu_hint')"
        >
            <DFormCheck
                id="is_eu"
                v-model="form.is_eu"
                inline
                :is-valid="isFormFieldValid('is_eu')"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.countries.form.blocked')"
            :error="errors.blocked"
            :hint="__('settings.countries.form.blocked_hint')"
        >
            <DFormCheck
                id="is_eu"
                v-model="form.blocked"
                inline
                :is-valid="isFormFieldValid('blocked')"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.countries.form.state_required')"
            :error="errors.blocked"
            :hint="__('settings.countries.form.state_required_hint')"
        >
            <DFormCheck
                id="state_required"
                v-model="form.state_required"
                inline
                :is-valid="isFormFieldValid('state_required')"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.countries.form.local_currency_hint_enabled')"
            :error="errors.local_currency_hint_enabled"
            :hint="__('settings.countries.form.local_currency_hint_enabled_hint')"
        >
            <DFormCheck
                id="local_currency_hint_enabled"
                v-model="form.local_currency_hint_enabled"
                inline
                :is-valid="isFormFieldValid('local_currency_hint_enabled')"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormRow
            v-if="form.local_currency_hint_enabled"
            :label="__('settings.countries.form.local_currency_hint_code')"
            :hint="__('settings.countries.form.local_currency_hint_code_hint')"
        >
            <DSelect
                id="local_currency_hint_code"
                v-model="form.local_currency_hint_code"
                :options="currencyCodes"
                :is-valid="isFormFieldValid('local_currency_hint_code')"
                :invalid-feedback="errors.local_currency_hint_code"
                :disabled="onlyShowForm"
            />
        </DFormRow>

        <DFormControls
            v-if="!onlyShowForm"
            :cancel-route="$route('settings.countries.index')"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
    submitUrl: {
        type: String,
        default: ''
    },
    country: {
        type: Object,
        default: () => ({})
    },
    errors: {
        type: Object,
        default: () => ({})
    },
    onlyShowForm: {
        type: Boolean,
        default: false,
    },
    currencyCodes: {
        type: Array,
        default: () => []
    }
})

const languages = [
    'de',
    'en',
    'da',
    'nl',
    'fi',
    'fr',
    'it',
    'no',
    'pr',
    'es',
    'sv',
    'sk',
    'cz',
    'rs',
]
const nameFields = {}

languages.forEach((language) => {
    nameFields[`name_${language}`] = ref(props.country[`name_${language}`] ?? null)
})

const form = useForm({
    country_code: ref(props.country?.country_code ?? null),
    ...nameFields,
    is_eu: ref(props.country?.is_eu ?? false),
    blocked: ref(props.country?.blocked ?? false),
    state_required: ref(props.country?.state_required ?? false),
    local_currency_hint_enabled: ref(props.country?.local_currency_hint_enabled ?? false),
    local_currency_hint_code: ref(props.country?.local_currency_hint_code ?? null),
})

const isCountryCodeDisabled = () => {
    return !!props.country?.country_code
}

const emit = defineEmits(['form-submitted'])

const submitForm = () => {
    emit('form-submitted', form)
}
</script>
